import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { hotArticles, categories, reviews } from "@/mocks/articles";

function HotArticlesSlider() {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (dir: "left" | "right") => {
    if (!scrollRef.current) return;
    const amount = 340;
    scrollRef.current.scrollBy({ left: dir === "left" ? -amount : amount, behavior: "smooth" });
  };

  return (
    <section className="py-14 bg-background-100">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
              <span className="text-primary-500">🔥</span> 热门瓜料
            </h2>
            <p className="text-sm text-foreground-500 mt-1">实时追踪全网最火爆的吃瓜热点</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse"></span>
              实时热榜
            </span>
            <div className="flex gap-1">
              <button onClick={() => scroll("left")} className="w-9 h-9 rounded-full bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors">
                <i className="ri-arrow-left-s-line text-foreground-700"></i>
              </button>
              <button onClick={() => scroll("right")} className="w-9 h-9 rounded-full bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors">
                <i className="ri-arrow-right-s-line text-foreground-700"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Slider */}
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 -mx-4 px-4 md:px-6 snap-x snap-mandatory"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {hotArticles.map((article) => (
            <Link
              key={article.id}
              to={`/article/${article.id}`}
              className="flex-shrink-0 w-[300px] snap-start group cursor-pointer"
            >
              <div className="bg-background-50 rounded-xl overflow-hidden transition-all duration-300 group-hover:scale-[1.02]">
                <div className="relative w-full h-[200px] overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                    title={article.title}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                  {article.isExclusive && (
                    <span className="absolute top-3 left-3 px-2 py-1 rounded-md bg-accent-500 text-foreground-900 text-[10px] font-bold">
                      独家
                    </span>
                  )}
                  {article.isHot && (
                    <span className="absolute top-3 right-3 px-2 py-1 rounded-md bg-primary-500 text-background-50 text-[10px] font-bold">
                      热门
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[11px] text-primary-600 font-medium bg-primary-50 px-2 py-0.5 rounded-full">
                      {article.category}
                    </span>
                    <span className="text-[11px] text-foreground-400">{article.author}</span>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 leading-snug mb-2 group-hover:text-primary-600 transition-colors">
                    {article.title}
                  </h3>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-400">
                      {new Date(article.publishedAt).toLocaleDateString("zh-CN")}
                    </span>
                    <span className="text-lg font-bold text-primary-500">
                      {article.heat > 10000
                        ? `${(article.heat / 10000).toFixed(1)}万`
                        : article.heat}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function CategoryGrid() {
  return (
    <section className="py-14 bg-background-50">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
              精选频道
            </h2>
            <p className="text-sm text-foreground-500 mt-1">按频道分类浏览，精准定位你感兴趣的瓜</p>
          </div>
          <Link to="/category/star-gossip" className="hidden sm:flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap">
            浏览全部频道 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="group cursor-pointer"
            >
              <div className="relative w-full aspect-[3/2] rounded-xl overflow-hidden mb-3">
                <img
                  src={cat.image}
                  alt={cat.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  title={`${cat.name} - 51吃瓜网`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                {cat.isHot && (
                  <span className="absolute top-3 right-3 px-2 py-0.5 rounded-md bg-primary-500 text-background-50 text-[10px] font-bold">
                    热门
                  </span>
                )}
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-md bg-white/20 flex items-center justify-center flex-shrink-0">
                      <i className={`${cat.icon} text-white text-xs`}></i>
                    </div>
                    <div>
                      <h3 className="text-white text-sm font-bold leading-tight">{cat.name}</h3>
                      <p className="text-white/70 text-[11px]">共 {cat.count} 篇爆料</p>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function ReviewSection() {
  return (
    <section className="py-14 bg-background-100">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="mb-8">
          <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold mb-3">
            瓜友辣评
          </span>
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
            吃瓜群众都在说
          </h2>
          <p className="text-sm text-foreground-500 mt-1">来自真实瓜友的评价与反馈</p>
        </div>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4">
          {reviews.map((review) => (
            <div key={review.id} className="break-inside-avoid mb-4">
              <div className="bg-background-50 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                    <img
                      src={review.avatar}
                      alt={review.username}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-900">{review.username}</div>
                    <div className="text-xs text-foreground-400">{review.handle}</div>
                  </div>
                  <div className="ml-auto flex items-center gap-0.5">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                    ))}
                  </div>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  &ldquo;{review.content}&rdquo;
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaSection() {
  return (
    <section className="relative py-20 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20warm%20golden%20sparkle%20lights%20background%2C%20soft%20bokeh%20effect%2C%20luxurious%20ambient%20glow%2C%20dark%20warm%20atmosphere%2C%20no%20text%2C%20artistic%20composition%2C%20cinematic%20lighting%2C%20elegant%20golden%20particles&width=1600&height=600&seq=home-cta-bg&orientation=landscape"
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
      </div>
      <div className="relative max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-3">
              有料你就来
            </h2>
            <p className="text-white/70 text-sm md:text-base leading-relaxed max-w-md">
              你是圈内人？知道独家内幕？欢迎匿名投稿，一经采用稿费从优。
            </p>
          </div>
          <Link
            to="/about"
            className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white text-foreground-900 font-semibold text-sm hover:bg-accent-100 transition-all duration-300 cursor-pointer group whitespace-nowrap"
          >
            匿名投稿
            <span className="w-8 h-8 rounded-full bg-foreground-900 flex items-center justify-center group-hover:translate-x-0.5 transition-transform">
              <i className="ri-arrow-right-line text-white text-sm"></i>
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative w-full h-[500px] md:h-[620px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20dramatic%20red%20carpet%20event%20atmosphere%20with%20warm%20spotlights%20and%20golden%20sparkles%2C%20entertainment%20industry%20glamour%2C%20cinematic%20stage%20lighting%2C%20dark%20ambient%20background%2C%20no%20text%2C%20elegant%20luxurious%20composition%2C%20soft%20bokeh%20effect&width=1800&height=900&seq=home-hero-bg&orientation=landscape"
            alt="51吃瓜网 - 热门瓜料实时更新"
            className="w-full h-full object-cover object-top"
            title="51吃瓜网 - 最新热门瓜料实时更新的吃瓜爆料平台"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/45"></div>
        </div>
        <div className="relative w-full max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-500/20 backdrop-blur-sm border border-accent-500/30 text-accent-300 text-xs font-semibold mb-6">
              <span className="w-2 h-2 rounded-full bg-accent-400 animate-pulse"></span>
              24小时实时追踪
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-black text-white leading-tight mb-4">
              全网热点瓜料
              <br />
              <span className="text-accent-400">实时追踪</span> 不错过
            </h1>
            <p className="text-white/70 text-base md:text-lg max-w-lg mb-8 leading-relaxed">
              覆盖明星八卦、恋情曝光、综艺内幕等吃瓜热点，每日推送独家爆料资讯，做最懂你的吃瓜平台。
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/category/star-gossip"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-fire-line"></i>
                立即开瓜
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/10 backdrop-blur-sm text-white border border-white/20 font-medium text-sm hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-information-line"></i>
                了解更多
              </Link>
            </div>
          </div>
        </div>
      </section>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "51吃瓜网 - 最新热门瓜料实时更新的吃瓜爆料平台",
        "description": "51吃瓜网实时更新全网热门瓜料与娱乐爆料，覆盖明星八卦、恋情曝光、综艺内幕等吃瓜热点，每日推送独家爆料资讯。",
        "url": "http://csxsjds.cn/",
        "publisher": {
          "@type": "Organization",
          "name": "51吃瓜网",
          "url": "http://csxsjds.cn/"
        }
      }) }} />

      <HotArticlesSlider />
      <CategoryGrid />
      <ReviewSection />
      <CtaSection />
      <Footer />
    </div>
  );
}
